import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Logo from '../../../assets/logo.svg';
import SidebarSectionWrapper from './SidebarSectionWrapper';
import SidebarDivider from '../../../components/SidebarDivider';
import useQueryData from '../../../queries/useQueryData';
import {
  CUSTOMER_QUERY_KEY,
  CUSTOMERS_QUERY_KEY,
  ENVIRONMENT_NAME_QUERY_KEY,
} from '../../../constants/query';
import { companyNameSelector } from '../../../selectors/customer';
import { PORTAL_ROUTE } from '../../../constants/routes';
import { customersPagesSelectors } from '../../../selectors/customers';
import { pagesText } from '../../../constants/pagesText';
import ApplicationsService from '../../../services/applications.service';
import useSubscribeStorage from 'src/hooks/useSubscribeStorage';
import { DEFAULT_ENVIRONMENT_ID } from 'src/constants/storage';

const LinkTitle = styled(Link)({
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '28px',
  fontFamily: 'Inter',
  color: '#87C8E0',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'block',
});

const Subheader = styled(Typography)({
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '22px',
  fontFamily: 'Inter',
  color: '#87C8E0',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const Card = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFFFFF0A',
  padding: theme.spacing(1.375, 3),
  borderRadius: theme.spacing(1),
}));

const SidebarHeader = () => {
  const companyName = useQueryData(CUSTOMER_QUERY_KEY, companyNameSelector);
  const customersPages = useQueryData(CUSTOMERS_QUERY_KEY, customersPagesSelectors);
  const customersListCount = useMemo(() => {
    if (!customersPages?.length) return 0;

    return customersPages[0]?.customers?.length;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customersPages?.pages?.length]);
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const { data: environment } = useQuery([ENVIRONMENT_NAME_QUERY_KEY, customerEnvId], () =>
    ApplicationsService.getEnvironment(customerEnvId),
  );

  const companyNavigationLink = useMemo(() => {
    if (customersListCount === 1) return `${PORTAL_ROUTE}/company/edit`;

    return `${PORTAL_ROUTE}/companies`;
  }, [customersListCount]);

  const environmentName = useMemo(
    () => (environment?.name ? `Your Environment: ${environment.name}` : ''),
    [environment?.name],
  );

  return (
    <>
      <SidebarSectionWrapper>
        <Stack direction="column" spacing={3}>
          <Link to={`${PORTAL_ROUTE}/apps`} component={RouterLink}>
            <img src={Logo} width={50} height={42} alt="logo" />
          </Link>
          {(companyName || !!customersListCount || environmentName) && (
            <Stack gap={1} mt={1}>
              {companyNavigationLink && (companyName || !!customersListCount) && (
                <Card>
                  <LinkTitle to={companyNavigationLink} underline="none" component={RouterLink}>
                    {companyName || pagesText.selectCompany}
                  </LinkTitle>
                </Card>
              )}
              {environmentName && (
                <Card>
                  <LinkTitle
                    to={`${PORTAL_ROUTE}/company/environments`}
                    underline="none"
                    component={RouterLink}>
                    <Tooltip title={environmentName} arrow disableHoverListener={!environmentName}>
                      <Subheader>{environmentName}</Subheader>
                    </Tooltip>
                  </LinkTitle>
                </Card>
              )}
            </Stack>
          )}
        </Stack>
      </SidebarSectionWrapper>
      <SidebarDivider />
    </>
  );
};

export default SidebarHeader;
